import React, { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Camera, Heart } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { photoSrc } from "@/components/Stars";
import LikeButton from "@/components/LikeButton";
import PhotographerBadge from "@/components/PhotographerBadge";
import ShareMedia from "@/components/ShareMedia";
import Postcard from "@/components/Postcard";
import ReportButton from "@/components/ReportButton";
import { byLikes } from "@/lib/useLikes";
import { trackEvent } from "@/lib/analytics";

const FADE = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const POP = { initial: { scale: 0.96, opacity: 0 }, animate: { scale: 1, opacity: 1 }, transition: { duration: 0.2 } };
const NAV_BTN = "absolute top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-sand/10 hover:bg-sand/25 text-sand flex items-center justify-center";

function useLightboxKeys(open, close, step, isRtl) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(isRtl ? -1 : 1);
      if (e.key === "ArrowLeft") step(isRtl ? 1 : -1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, step, isRtl]);
}

function Lightbox({ photo, idx, total, close, step, onLike, countryName }) {
  const { t } = useLang();
  const stop = (e) => e.stopPropagation();
  return (
    <motion.div {...FADE} className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-sm flex items-center justify-center" onClick={close} role="dialog" aria-modal="true" data-testid="gallery-lightbox">
      <button onClick={close} aria-label={t("close")} className="absolute top-4 end-4 w-10 h-10 rounded-full bg-sand/10 hover:bg-sand/25 text-sand flex items-center justify-center" data-testid="gallery-close">
        <X className="w-5 h-5" />
      </button>
      {total > 1 && (
        <>
          <button onClick={(e) => { stop(e); step(-1); }} aria-label="prev" className={`${NAV_BTN} start-3`} data-testid="gallery-prev"><ChevronLeft className="w-6 h-6 rtl:rotate-180" /></button>
          <button onClick={(e) => { stop(e); step(1); }} aria-label="next" className={`${NAV_BTN} end-3`} data-testid="gallery-next"><ChevronRight className="w-6 h-6 rtl:rotate-180" /></button>
        </>
      )}
      <motion.figure key={photo.id} {...POP} className="max-w-[92vw] max-h-[88vh] flex flex-col items-center" onClick={stop}>
        <img src={photoSrc(photo.photo_url)} alt="" className="max-h-[72vh] max-w-full rounded-xl shadow-2xl object-contain" data-testid="gallery-image" />
        <figcaption className="mt-3 text-center text-sand">
          <div className="flex items-center justify-center gap-3">
            <div className="text-sm font-semibold flex items-center gap-1.5" data-testid="gallery-caption-author">{photo.author.name} <PhotographerBadge show={photo.is_photographer} /> · <span className="text-ochre">{"★".repeat(photo.rating)}</span></div>
            <LikeButton item={photo} onToggle={onLike} size="md" dark />
            <ShareMedia item={photo} countryName={countryName} dark size="md" />
            <Postcard item={photo} countryName={countryName} dark size="md" />
            <ReportButton item={photo} dark size="md" onHidden={close} />
          </div>
          <p className="text-xs text-sand/70 mt-1 max-w-xl line-clamp-2" dir="auto">{photo.text}</p>
          <div className="text-[10px] text-sand/50 mt-1">{idx + 1} / {total}</div>
        </figcaption>
      </motion.figure>
    </motion.div>
  );
}

export default function PhotoGallery({ reviews, code, onLike, openId, countryName }) {
  const { t, isRtl } = useLang();
  const photos = reviews.filter((r) => r.photo_url).sort(byLikes);
  const [idx, setIdx] = useState(null);
  const openIdx = photos.findIndex((p) => p.id === openId);

  const close = useCallback(() => setIdx(null), []);
  const step = useCallback((d) => setIdx((i) => (i === null ? i : (i + d + photos.length) % photos.length)), [photos.length]);
  useLightboxKeys(idx !== null, close, step, isRtl);
  useEffect(() => { if (openIdx >= 0) setIdx(openIdx); }, [openIdx]);

  if (photos.length === 0) return null;

  return (
    <div className="mb-4" data-testid="photo-gallery">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">
        <Camera className="w-3.5 h-3.5" /> {t("gallery")} <span className="text-ochre">({photos.length})</span>
        <span className="ms-auto normal-case tracking-normal flex items-center gap-1"><Heart className="w-3 h-3 text-rose-500" fill="currentColor" /> {t("sorted_by_likes")}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto thin-scroll pb-1 -mx-1 px-1">
        {photos.map((p, i) => (
          <div key={p.id} className="relative shrink-0">
            <button onClick={() => { setIdx(i); trackEvent("gallery_open", { country_code: code }); }} aria-label={p.author.name} data-testid={`gallery-thumb-${p.id}`}
              className="block w-24 h-24 rounded-xl overflow-hidden border border-stoneLine hover:border-ochre transition-colors group">
              <img src={photoSrc(p.photo_url)} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            </button>
            <LikeButton item={p} onToggle={onLike} className="absolute bottom-1 start-1" />
          </div>
        ))}
      </div>
      <AnimatePresence>
        {idx !== null && <Lightbox photo={photos[idx]} idx={idx} total={photos.length} close={close} step={step} onLike={onLike} countryName={countryName} />}
      </AnimatePresence>
    </div>
  );
}
